/**
 * The join link in Add machine (ADR 0090 decision 2): after the download button, the link is on
 * the clipboard (or a Copy link button when the browser refused), with a QR code for a phone's
 * camera, and this person's open links, each with Cancel link. Hidden while there is nothing to
 * show: no link made and none open.
 */
import { Copy, Link2 } from '@orgus/ui/icons';
import { Button, ErrorState, QrCode, RowStatus, Skeleton, formatClock } from '@orgus/ui';
import { JOIN_LINK_COPIED, JOIN_LINK_NOT_COPIED, ticketExpiry, ticketsSummary } from './joinTickets.ts';
import type { JoinTicketsState } from './useJoinTickets.ts';

/** Is there anything to show? */
export function joinPanelVisible(s: Pick<JoinTicketsState, 'on' | 'link' | 'list'>): boolean {
  if (!s.on) return false;
  if (s.link.kind !== 'idle') return true;
  return s.list.kind === 'error' || (s.list.kind === 'ready' && s.list.tickets.length > 0);
}

export function JoinLinkPanel({ s }: { s: JoinTicketsState }) {
  if (!joinPanelVisible(s)) return null;
  const { link, list } = s;
  const made = link.kind === 'copied' || link.kind === 'not-copied' ? link.link : null;
  const summary = list.kind === 'ready' ? ticketsSummary(list.tickets, s.now) : null;
  return (
    <section aria-label="Join link" className="flex flex-col gap-3 rounded-card border border-border-subtle p-4" data-testid="join-link">
      {link.kind === 'making' && (
        <div role="status" aria-live="polite">
          <RowStatus tone="working" icon="working" label="Making a join link" />
        </div>
      )}
      {link.kind === 'error' && <ErrorState variant="inline" error={link.error} onRetry={s.make} />}
      {made && (
        <div className="flex items-start gap-4" data-testid="join-link-made">
          <QrCode value={made.url} label="QR code of the join link, for a phone's camera" className="size-28" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="text-ui text-fg-muted" role="status" aria-live="polite">
              {link.kind === 'copied' ? JOIN_LINK_COPIED : JOIN_LINK_NOT_COPIED}
            </p>
            {link.kind === 'not-copied' && (
              <code className="select-all break-all rounded-control border border-border bg-bg px-2 py-1 font-mono text-caption text-fg" aria-label="Join link">
                {made.url}
              </code>
            )}
            <div>
              <Button variant="secondary" size="sm" icon={<Copy />} onClick={() => void s.copy()}>
                Copy link
              </Button>
            </div>
          </div>
        </div>
      )}
      {list.kind === 'loading' && (
        <div role="status" aria-label="Loading open links">
          <Skeleton className="h-4 w-48" />
        </div>
      )}
      {list.kind === 'error' && <ErrorState variant="inline" error={list.error} onRetry={s.reload} />}
      {list.kind === 'ready' && list.tickets.length > 0 && (
        <ul className="flex flex-col" aria-label="Open join links" data-testid="join-tickets">
          {list.tickets.length > 1 && <li className="flex h-row items-center text-caption text-fg-subtle num">{summary}</li>}
          {list.tickets.map((t) => (
            <li key={t.id} className="flex h-row items-center gap-3">
              <Link2 className="size-4 shrink-0 text-fg-subtle" aria-hidden />
              {list.tickets.length === 1 ? (
                <span className="min-w-0 flex-1 truncate text-ui text-fg-muted num">{summary}</span>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-ui text-fg-muted">Made at {formatClock(t.createdAt)}</span>
                  <span className="text-caption text-fg-subtle num">{ticketExpiry(t, s.now)}</span>
                </>
              )}
              <Button variant="ghost" size="sm" loading={s.cancelling === t.id} disabled={s.cancelling !== null} onClick={() => void s.cancel(t.id)}>
                Cancel link
              </Button>
            </li>
          ))}
        </ul>
      )}
      {s.cancelError && <ErrorState variant="inline" error={s.cancelError} />}
    </section>
  );
}
